import { Link } from 'react-router-dom';
import { events } from '@/mocks/events';
import { eventImgs } from '@/mocks/images';
import { ProgressBadge, SectionHead } from '@/components/feature/Atoms';
import { usePageMeta } from '@/hooks/useSeo';

const timeline = [
  { time: '0-3 小时', text: '首发：单一信源在熟人圈扩散，尚无二手验证，信息处于“未经证实”状态。', tag: '发酵' },
  { time: '3-12 小时', text: '放大：关键账号转发、情绪化标题刷屏，打开率飙升的同时失真风险同步累积。', tag: '扩散' },
  { time: '12-24 小时', text: '沸腾：进入热搜与评论区共振，点赞、转发、跟帖相互强化，矛盾被推到顶点。', tag: '沸腾' },
  { time: '24-72 小时', text: '质疑：多方信源陆续出现矛盾，原始片段被重新拼合，越来越多读者开始要求“完整上下文”。', tag: '质疑' },
  { time: '72 小时+', text: '澄清：权威或一手信息发布，形成终局口径；此前的快速传播链开始被逐条修正。', tag: '澄清' },
];

const reversalWays = [
  {
    no: '壹',
    name: '断章取义',
    text: '一段完整对话被裁剪成只利单方叙事的片段，最关键的上下文在转发中被悄悄丢掉，真相就藏在被剪掉的那几句里。',
  },
  {
    no: '贰',
    name: '时间错位',
    text: '旧截图换个时间戳重发，就能制造“刚刚发生”的错觉。事件的时间顺序一旦被搅乱，前因后果就无从谈起。',
  },
  {
    no: '叁',
    name: '来路不明',
    text: '所谓内部信源、匿名爆料缺乏可核验的出处，却靠情绪驱动获得海量转发。来源越模糊，越需要警惕。',
  },
];

const schema = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: '三步复盘法：看清一场互联网事件的前因后果',
  description: '从时间线、反转节点、各方回应到后续进展，一套可复用的复盘框架，帮你把每一场反转看个明白。',
  author: { '@type': 'Organization', name: '99吃瓜编辑团队' },
  datePublished: '2026-08-18',
  inLanguage: 'zh-CN',
};

export default function Article() {
  usePageMeta(
    '三步复盘法：看清一场互联网事件的前因后果 - 99吃瓜',
    '从事件发酵到澄清的完整时间线、实锤翻车的三种路径、各方回应的三层结构，以及如何把后续进展跟完——一套可复用的理性吃瓜复盘框架。',
  );

  const related = [events[2], events[7], events[6]];

  return (
    <div className="py-12 md:py-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <nav aria-label="面包屑" className="mx-auto max-w-3xl">
          <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/review" className="hover:text-primary-600">
                复盘档案
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li aria-current="page" className="text-foreground-700">
              深度复盘
            </li>
          </ol>
        </nav>
      </div>

      <div className="mx-auto mt-8 grid max-w-[1280px] gap-10 px-4 md:px-6 lg:grid-cols-[1fr_260px]">
        {/* 正文 */}
        <article className="min-w-0">
          <header>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700">复盘方法论</span>
              <ProgressBadge label="已复盘" />
              <span className="text-xs text-foreground-500">2026-08-18 · 阅读 12 分钟</span>
            </div>
            <h1 className="mt-4 text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
              三步复盘法：看清一场互联网事件的前因后果
            </h1>
            <p id="lede" className="mt-4 text-base leading-8 text-foreground-600 md:text-lg">
              每当新的“吃瓜”冲上热搜，先别急着站队。真正有价值的，是弄清它从何而来、为何反转、各方如何回应、后续又走向何方。本文把这一整套流程归纳成
              时间线、反转节点、回应与进展 三个步骤，你可以直接拿去复用。
            </p>
          </header>

          {/* 第一步：时间线 */}
          <section id="timeline" className="section-anchor mt-12 border-t border-background-200 pt-8">
            <h2 className="flex items-center gap-2 text-2xl font-black text-foreground-950">
              <span className="text-primary-500">壹</span> 事件脉络：一条时间线看懂前因后果
            </h2>
            <p className="mt-4 leading-7 text-foreground-700">
              复盘的第一步，是把散落各处的信息按时间顺序排好。事件的“前因后果”不是一句口号，而是一条可回放的时间轴——谁先发声、谁在跟进、哪个节点开始出现矛盾，都会在这条线上显出原形。
            </p>
            <ol className="mt-7 space-y-0">
              {timeline.map((t) => (
                <li key={t.time} className="relative flex gap-5 pb-8 last:pb-0">
                  <span className="relative z-10 mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-background-50">
                    {t.tag}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">{t.time}</p>
                    <p className="mt-1 text-sm leading-7 text-foreground-700 md:text-base">{t.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-2 text-sm leading-7 text-foreground-600">
              当你把事件压缩到一条时间线上，就会发现：很多“反转”其实早在发酵阶段就已埋下伏笔——缺失的上下文、错位的时间、来路不明的信源，都是后来翻车的种子。
            </p>
          </section>

          {/* 第二步：反转梳理 */}
          <section id="reversal" className="section-anchor mt-10 border-t border-background-200 pt-8">
            <h2 className="flex items-center gap-2 text-2xl font-black text-foreground-950">
              <span className="text-primary-500">贰</span> 反转梳理：实锤从诞生到翻车的三种路径
            </h2>
            <p className="mt-4 leading-7 text-foreground-700">
              当截图、录音、聊天记录被奉为“实锤”时，距离真相反而可能更远。复盘里最常见的“反转”，几乎都逃不出下面三种翻车路径。
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {reversalWays.map((w) => (
                <div key={w.no} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="font-brand text-2xl text-primary-400">{w.no}</span>
                  <h3 className="mt-2 text-base font-bold text-foreground-950">{w.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{w.text}</p>
                </div>
              ))}
            </div>
            <blockquote className="mt-6 border-l-2 border-primary-500 bg-primary-50/70 px-5 py-4 text-base font-medium leading-7 text-foreground-800">
              “反转并不是事件的失败，而是信息补全到一定程度的必然。真相从来只奖励耐心。”
            </blockquote>
          </section>

          {/* 配图 */}
          <figure className="mt-10 overflow-hidden rounded-lg border border-background-200">
            <img
              src={eventImgs['evidence-collapse']}
              alt="用放大镜核查被裁剪的聊天记录截图，还原事件完整上下文的编辑插画"
              className="h-64 w-full object-cover object-top md:h-80"
            />
            <figcaption className="bg-background-100 px-5 py-3 text-xs text-foreground-500">
              拆解“实锤”第一步：永远先问，完整上下文在哪。
            </figcaption>
          </figure>

          {/* 第三步：各方回应与后续进展 */}
          <section id="responses" className="section-anchor mt-10 border-t border-background-200 pt-8">
            <h2 className="flex items-center gap-2 text-2xl font-black text-foreground-950">
              <span className="text-primary-500">叁</span> 各方回应：三层结构看清诚意
            </h2>
            <p className="mt-4 leading-7 text-foreground-700">
              同一件事，当事各方先后发声，口径可能南辕北辙。与其听情绪，不如把每份回应拆成三层来读。
            </p>
            <div className="mt-6 space-y-4">
              {[
                { layer: '第一层 · 一句话回应', text: '表态越快越简洁越好；含糊其辞往往被公众解读为“默认”，反而火上浇油。', icon: 'ri-chat-1-line' },
                { layer: '第二层 · 书面声明', text: '重点看是否补充事实、时间节点与可验证的信源。空洞的“深表遗憾、正在核实”不等于有信息量。', icon: 'ri-file-text-line' },
                { layer: '第三层 · 行动回应', text: '表态之后是否真的致歉、整改、追责，才是衡量各方回应诚意与后续进展的最终标尺。', icon: 'ri-check-double-line' },
              ].map((r) => (
                <div key={r.layer} className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-lg text-accent-600">
                    <i className={r.icon} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-950">{r.layer}</h3>
                    <p className="mt-1 text-sm leading-6 text-foreground-600">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="progress" className="section-anchor mt-10 border-t border-background-200 pt-8">
            <h2 className="flex items-center gap-2 text-2xl font-black text-foreground-950">
              <span className="text-primary-500">肆</span> 后续进展：怎么才算把一件事跟完
            </h2>
            <p className="mt-4 leading-7 text-foreground-700">
              追热点不是看完热搜就结束。我们建议用“记录、核实、等待、回看”四步，让每次复盘都有始有终。
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { n: '01', t: '记录', d: '建一份时间轴，把时间、信源、关键事实依次排好。' },
                { n: '02', t: '核实', d: '为核心信息标注来源，分清一手与二手转述。' },
                { n: '03', t: '等待', d: '没有新事实前允许结论悬置，不急于盖棺定论。' },
                { n: '04', t: '回看', d: '事件收尾后再通读，看清自己曾被哪一段带偏。' },
              ].map((s) => (
                <div key={s.n} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="font-heading text-sm font-black text-accent-500">{s.n}</span>
                  <h3 className="mt-2 text-base font-bold text-foreground-950">{s.t}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{s.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-7 text-foreground-600">
              复盘真正的意义，不在于证明谁对谁错，而在于让你下次看到类似“反转”时，能多一分从容、少一分冲动。想看更多逐条拆解，可进入
              <Link to="/review" className="mx-1 font-semibold text-primary-600 hover:text-primary-700">
                复盘档案
              </Link>
              按栏目阅读。
            </p>
          </section>

          {/* 延伸阅读 */}
          <section className="mt-12 border-t border-background-200 pt-8">
            <SectionHead kicker="延伸阅读" title="继续往下看" />
            <div className="mt-6 space-y-3">
              {related.map((e) => (
                <Link
                  key={e.slug}
                  to="/review"
                  className="group flex flex-wrap items-center justify-between gap-2 rounded-lg border border-background-200 bg-background-100 px-5 py-4 transition-colors hover:border-primary-300"
                >
                  <span className="min-w-0 text-sm font-medium text-foreground-800 group-hover:text-primary-700 md:text-base">
                    {e.title}
                  </span>
                  <span className="flex items-center gap-3 whitespace-nowrap text-xs text-foreground-500">
                    {e.kicker}
                    <i className="ri-arrow-right-line text-primary-600" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </article>

        {/* 内容目录 */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24 rounded-lg border border-background-200 bg-background-100/70 p-5" aria-label="文章目录">
            <p className="text-xs font-semibold uppercase tracking-widest text-foreground-500">文章目录</p>
            <ul className="mt-4 space-y-1 text-sm">
              {[
                { href: '#timeline', label: '壹 · 事件脉络' },
                { href: '#reversal', label: '贰 · 反转梳理' },
                { href: '#responses', label: '叁 · 各方回应' },
                { href: '#progress', label: '肆 · 后续进展' },
              ].map((a) => (
                <li key={a.href}>
                  <a href={a.href} className="block rounded-md px-3 py-2 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700">
                    {a.label}
                  </a>
                </li>
              ))}
            </ul>
            <Link
              to="/review"
              className="mt-4 inline-flex items-center gap-1 border-t border-background-200 pt-4 text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              回到复盘档案 <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </nav>
        </aside>
      </div>
    </div>
  );
}